
import React from "react";

interface LogoProps {
  className?: string;
  showText?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  showTagline?: boolean;
  variant?: "light" | "dark";
  useAltIcon?: boolean;
}

export const Logo = React.memo(function Logo({ 
  className, 
  showText = true, 
  size = "md", 
  showTagline = false,
  variant = "light",
  useAltIcon = false
}: LogoProps) {
  const sizes = {
    sm: { icon: 24, text: "text-lg", tagline: "text-xs" },
    md: { icon: 32, text: "text-xl", tagline: "text-sm" },
    lg: { icon: 40, text: "text-2xl", tagline: "text-base" },
    xl: { icon: 64, text: "text-4xl", tagline: "text-lg" },
    "2xl": { icon: 96, text: "text-5xl md:text-6xl", tagline: "text-xl" }, // Increased size for hero section
  };

  const colorScheme = {
    light: {
      intelli: "text-yarn-orchid",
      stitch: "text-[#393C44]", // Dark charcoal for main text
      tagline: "text-muted-foreground"
    },
    dark: {
      intelli: "text-yarn-orchid",
      stitch: "text-white", // Changed to white for better contrast on darker footer
      tagline: "text-white/80"  // Updated to white with opacity for better readability
    }
  };

  // Preload the logo image
  const iconSrc = useAltIcon 
    ? "/images/018392d2-435b-4790-82f1-bd009dffb6aa.png"
    : "/images/5d0fc9cc-bda6-4e73-9754-4c73e92888d6.png";

  React.useEffect(() => {
    // Preload the image to improve LCP
    const img = new Image();
    img.src = iconSrc;
  }, [iconSrc]);

  return (
    <div className={`flex items-center ${className}`}>
      <div className="relative print:inline-block print:visible print:opacity-100" 
           style={{ width: `${sizes[size].icon}px`, height: `${sizes[size].icon}px` }}>
        <img 
          src={iconSrc} 
          alt="IntelliStitch Logo" 
          width={sizes[size].icon} 
          height={sizes[size].icon}
          className="text-primary print:inline-block print:visible print:opacity-100"
          style={{ display: 'inline-block' }}
          loading="eager"
          decoding="async"
        />
      </div>
      {showText && (
        <div className="flex flex-col ml-2.5">
          <span className={`font-bold ${sizes[size].text} flex items-center tracking-tight`}>
            <span className={colorScheme[variant].intelli}>Intelli</span>
            <span className={`print:text-[#403E43] ${colorScheme[variant].stitch}`}>Stitch</span>
          </span>
          
          {showTagline && (
            <span className={`${colorScheme[variant].tagline} ${sizes[size].tagline} text-nowrap leading-tight mt-0.5`}>
              You knit. We calculate.
            </span>
          )}
        </div>
      )}
    </div>
  );
});
